<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import orderIssueTagsAPI from 'dashboard/api/orderIssueTags';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

// State
const loading = ref(false);
const problemTags = ref([]);
const actionTags = ref([]);
const combinations = ref([]);

// Hover state for highlighting connections
const hoveredProblemId = ref(null);
const hoveredActionId = ref(null);
const hoveredConnectionKey = ref(null);

// New tag form
const showNewTagForm = ref(false);
const newTagForm = ref({
  name: '',
  tag_type: 'problem',
});

// Refs for position calculation
const containerRef = ref(null);
const problemRefs = ref({});
const actionRefs = ref({});

// Fetch all tags and combinations
const fetchTags = async () => {
  loading.value = true;
  try {
    const response = await orderIssueTagsAPI.getAll();
    problemTags.value = response.data.problem_tags || [];
    actionTags.value = response.data.action_tags || [];
    combinations.value = response.data.combinations || [];
  } catch (error) {
    console.error('Failed to fetch tags:', error);
    useAlert(t('ORDER_ISSUES.TAG_MANAGEMENT.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

// Check if a combination exists
const hasCombination = (problemTagId, actionTagId) => {
  return combinations.value.some(
    c => c.problem_tag_id === problemTagId && c.action_tag_id === actionTagId
  );
};

// Get combination info
const getCombination = (problemTagId, actionTagId) => {
  return combinations.value.find(
    c => c.problem_tag_id === problemTagId && c.action_tag_id === actionTagId
  );
};

// Check if a connection should be highlighted
const isConnectionHighlighted = (problemId, actionId) => {
  if (hoveredConnectionKey.value === `${problemId}-${actionId}`) return true;
  if (hoveredProblemId.value === problemId && hasCombination(problemId, actionId)) return true;
  if (hoveredActionId.value === actionId && hasCombination(problemId, actionId)) return true;
  return false;
};

// Check if a tag should be highlighted
const isProblemHighlighted = (problemId) => {
  if (hoveredProblemId.value === problemId) return true;
  if (hoveredActionId.value && hasCombination(problemId, hoveredActionId.value)) return true;
  if (hoveredConnectionKey.value?.startsWith(`${problemId}-`)) return true;
  return false;
};

const isActionHighlighted = (actionId) => {
  if (hoveredActionId.value === actionId) return true;
  if (hoveredProblemId.value && hasCombination(hoveredProblemId.value, actionId)) return true;
  if (hoveredConnectionKey.value?.endsWith(`-${actionId}`)) return true;
  return false;
};

// Toggle combination between tags
const toggleCombination = async (problemTagId, actionTagId) => {
  const isLinked = hasCombination(problemTagId, actionTagId);
  const combo = getCombination(problemTagId, actionTagId);
  
  // Don't allow removing template-based combinations
  if (isLinked && combo?.from_template) {
    useAlert(t('ORDER_ISSUES.TAG_MANAGEMENT.CANNOT_REMOVE_TEMPLATE_COMBO'));
    return;
  }
  
  try {
    await orderIssueTagsAPI.updateCombination(problemTagId, actionTagId, !isLinked);
    await fetchTags();
  } catch (error) {
    useAlert(error.response?.data?.error || t('ORDER_ISSUES.TAG_MANAGEMENT.UPDATE_ERROR'));
  }
};

// Toggle protected status
const toggleProtected = async (tag) => {
  try {
    await orderIssueTagsAPI.toggleProtected(tag.id);
    await fetchTags();
  } catch (error) {
    useAlert(t('ORDER_ISSUES.TAG_MANAGEMENT.UPDATE_ERROR'));
  }
};

// Create new tag
const createTag = async () => {
  if (!newTagForm.value.name.trim()) {
    useAlert(t('ORDER_ISSUES.TAG_MANAGEMENT.NAME_REQUIRED'));
    return;
  }
  
  try {
    await orderIssueTagsAPI.create(newTagForm.value);
    await fetchTags();
    showNewTagForm.value = false;
    newTagForm.value = { name: '', tag_type: 'problem' };
    useAlert(t('ORDER_ISSUES.TAG_MANAGEMENT.TAG_CREATED'));
  } catch (error) {
    useAlert(error.response?.data?.errors?.[0] || t('ORDER_ISSUES.TAG_MANAGEMENT.CREATE_ERROR'));
  }
};

// Sync from templates
const syncFromTemplates = async () => {
  try {
    loading.value = true;
    await orderIssueTagsAPI.syncFromTemplates();
    await fetchTags();
    useAlert(t('ORDER_ISSUES.TAG_MANAGEMENT.SYNCED'));
  } catch (error) {
    useAlert(t('ORDER_ISSUES.TAG_MANAGEMENT.SYNC_ERROR'));
  } finally {
    loading.value = false;
  }
};

// Seed quality defaults
const seedQualityDefaults = async () => {
  try {
    loading.value = true;
    await orderIssueTagsAPI.seedQualityDefaults();
    await fetchTags();
    useAlert('Quality tags added successfully');
  } catch (error) {
    useAlert('Failed to add quality tags');
  } finally {
    loading.value = false;
  }
};

// Get connection count for a problem tag
const getConnectionCount = (problemTagId) => {
  return combinations.value.filter(c => c.problem_tag_id === problemTagId).length;
};

// Watch for modal open
watch(() => props.show, (newVal) => {
  if (newVal) {
    fetchTags();
    showNewTagForm.value = false;
    hoveredProblemId.value = null;
    hoveredActionId.value = null;
    hoveredConnectionKey.value = null;
  }
});

onMounted(() => {
  if (props.show) {
    fetchTags();
  }
});
</script>

<template>
  <Modal
    :show="show"
    :full-width="true"
    :show-close-button="false"
    :on-close="() => emit('close')"
  >
    <div class="relative flex flex-col h-[85vh] w-[90vw] max-w-[1400px]">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('ORDER_ISSUES.TAG_MANAGEMENT.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ $t('ORDER_ISSUES.TAG_MANAGEMENT.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <Button
            variant="faded"
            color="teal"
            size="sm"
            icon="i-lucide-package-check"
            @click="seedQualityDefaults"
            title="Add default Product Quality tags"
          >
            Add Quality Tags
          </Button>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-refresh-cw"
            @click="syncFromTemplates"
          >
            Sync from Templates
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            icon="i-lucide-plus"
            @click="showNewTagForm = true"
          >
            Add Tag
          </Button>
          <button
            class="text-n-slate-11 hover:text-n-slate-12 transition-colors ml-2"
            @click="emit('close')"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>
      </div>

      <!-- New tag form - Floating card style -->
      <div v-if="showNewTagForm" class="absolute inset-0 z-40" @click.self="showNewTagForm = false">
        <div class="absolute top-16 right-6 bg-n-solid-2 border border-n-weak rounded-xl shadow-2xl p-4 w-80 z-50">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-semibold text-n-slate-12">Create New Tag</h4>
            <button
              class="text-n-slate-10 hover:text-n-slate-12 transition-colors"
              @click="showNewTagForm = false"
            >
              <i class="i-lucide-x text-sm" />
            </button>
          </div>
          
          <div class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">Tag Name</label>
              <input
                v-model="newTagForm.name"
                type="text"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand placeholder:text-n-slate-9"
                placeholder="Enter tag name..."
                @keyup.enter="createTag"
                autofocus
              />
            </div>
            
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">Tag Type</label>
              <div class="flex gap-2">
                <button
                  class="flex-1 px-3 py-2 text-sm rounded-lg border transition-all flex items-center justify-center gap-2"
                  :class="[
                    newTagForm.tag_type === 'problem'
                      ? 'bg-n-ruby-3 border-n-ruby-7 text-n-ruby-11'
                      : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-slate-6'
                  ]"
                  @click="newTagForm.tag_type = 'problem'"
                >
                  <span class="w-2 h-2 rounded-full bg-n-ruby-9"></span>
                  Problem
                </button>
                <button
                  class="flex-1 px-3 py-2 text-sm rounded-lg border transition-all flex items-center justify-center gap-2"
                  :class="[
                    newTagForm.tag_type === 'action'
                      ? 'bg-n-amber-3 border-n-amber-7 text-n-amber-11'
                      : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-slate-6'
                  ]"
                  @click="newTagForm.tag_type = 'action'"
                >
                  <span class="w-2 h-2 rounded-full bg-n-amber-9"></span>
                  Action
                </button>
              </div>
            </div>
            
            <div class="flex gap-2 pt-1">
              <Button 
                variant="solid" 
                color="blue" 
                size="sm" 
                class="flex-1"
                :disabled="!newTagForm.name.trim()"
                @click="createTag"
              >
                Create Tag
              </Button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex-1 flex items-center justify-center">
        <span class="text-n-slate-11">Loading tags...</span>
      </div>

      <!-- Main content with connection visualization -->
      <div v-else ref="containerRef" class="flex-1 flex overflow-hidden">
        <!-- Problem Tags Column -->
        <div class="w-[320px] shrink-0 border-r border-n-weak overflow-y-auto p-4">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3 flex items-center gap-2 sticky top-0 bg-n-background py-2">
            <span class="w-3 h-3 rounded-full bg-n-ruby-9"></span>
            Problem Tags
            <span class="text-xs font-normal text-n-slate-11">({{ problemTags.length }})</span>
          </h3>
          <div class="space-y-1">
            <div
              v-for="(tag, index) in problemTags"
              :key="tag.id"
              :ref="el => { if (el) problemRefs[tag.id] = el }"
              class="group flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all"
              :class="[
                isProblemHighlighted(tag.id)
                  ? 'border-n-ruby-7 bg-n-ruby-3 shadow-sm' 
                  : 'border-transparent hover:border-n-slate-6 hover:bg-n-alpha-1'
              ]"
              @mouseenter="hoveredProblemId = tag.id"
              @mouseleave="hoveredProblemId = null"
            >
              <span
                v-if="tag.protected"
                class="text-n-amber-9 shrink-0"
                title="Protected"
              >
                <i class="i-lucide-shield text-sm" />
              </span>
              <span class="flex-1 text-sm text-n-slate-12 font-medium truncate">
                {{ tag.name }}
              </span>
              <span
                v-if="tag.from_template"
                class="text-n-slate-10 shrink-0"
                title="From template"
              >
                <i class="i-lucide-file-text text-xs" />
              </span>
              <span class="text-xs text-n-slate-11 bg-n-alpha-2 px-1.5 py-0.5 rounded shrink-0">
                {{ getConnectionCount(tag.id) }}
              </span>
              <button
                class="opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-amber-9 transition-all shrink-0"
                title="Toggle protection"
                @click.stop="toggleProtected(tag)"
              >
                <i :class="tag.protected ? 'i-lucide-shield-off' : 'i-lucide-shield'" class="text-sm" />
              </button>
            </div>
            <div v-if="problemTags.length === 0" class="text-sm text-n-slate-11 text-center py-8">
              No problem tags yet
            </div>
          </div>
        </div>

        <!-- Connection Lines Area -->
        <div class="flex-1 relative bg-n-alpha-1 overflow-y-auto">
          <!-- Connection grid -->
          <div class="p-6">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-4">
              Connections
              <span class="text-xs font-normal text-n-slate-11 ml-2">
                Click to connect/disconnect • Hover to highlight
              </span>
            </h3>
            
            <!-- Connection matrix -->
            <div class="space-y-1">
              <div
                v-for="combo in combinations"
                :key="`${combo.problem_tag_id}-${combo.action_tag_id}`"
                class="flex items-center gap-3 px-3 py-2 rounded-lg transition-all cursor-pointer"
                :class="[
                  isConnectionHighlighted(combo.problem_tag_id, combo.action_tag_id)
                    ? 'bg-n-teal-3 border border-n-teal-6'
                    : 'hover:bg-n-alpha-2'
                ]"
                @mouseenter="hoveredConnectionKey = `${combo.problem_tag_id}-${combo.action_tag_id}`"
                @mouseleave="hoveredConnectionKey = null"
                @click="toggleCombination(combo.problem_tag_id, combo.action_tag_id)"
              >
                <!-- Problem tag -->
                <span class="flex items-center gap-1.5 px-2 py-1 bg-n-ruby-3 border border-n-ruby-6 rounded text-sm text-n-ruby-11 min-w-[120px]">
                  <span class="w-2 h-2 rounded-full bg-n-ruby-9"></span>
                  <span class="truncate">{{ problemTags.find(t => t.id === combo.problem_tag_id)?.name }}</span>
                </span>
                
                <!-- Connection line -->
                <div class="flex-1 flex items-center gap-2">
                  <div class="flex-1 h-px bg-n-teal-6"></div>
                  <i 
                    class="i-lucide-link text-n-teal-9"
                    :class="{ 'text-n-slate-8': combo.from_template }"
                  />
                  <div class="flex-1 h-px bg-n-teal-6"></div>
                </div>
                
                <!-- Action tag -->
                <span class="flex items-center gap-1.5 px-2 py-1 bg-n-amber-3 border border-n-amber-6 rounded text-sm text-n-amber-11 min-w-[120px]">
                  <span class="w-2 h-2 rounded-full bg-n-amber-9"></span>
                  <span class="truncate">{{ actionTags.find(t => t.id === combo.action_tag_id)?.name }}</span>
                </span>
                
                <!-- Template indicator -->
                <span
                  v-if="combo.from_template"
                  class="text-xs text-n-slate-10 bg-n-alpha-2 px-1.5 py-0.5 rounded"
                  title="Created from template - cannot remove"
                >
                  template
                </span>
                <span
                  v-else
                  class="text-xs text-n-slate-10 opacity-0 group-hover:opacity-100"
                >
                  click to remove
                </span>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="combinations.length === 0" class="text-center py-12 text-n-slate-11">
              <i class="i-lucide-link-2-off text-4xl mb-3 block opacity-50" />
              <p>No connections yet</p>
              <p class="text-sm mt-1">Hover over tags to create connections</p>
            </div>

            <!-- Quick connect section -->
            <div v-if="hoveredProblemId || hoveredActionId" class="mt-6 p-4 bg-n-solid-2 rounded-lg border border-n-weak">
              <h4 class="text-sm font-medium text-n-slate-12 mb-3">
                Quick Connect: 
                <span v-if="hoveredProblemId" class="text-n-ruby-11">
                  {{ problemTags.find(t => t.id === hoveredProblemId)?.name }}
                </span>
                <span v-if="hoveredActionId" class="text-n-amber-11">
                  {{ actionTags.find(t => t.id === hoveredActionId)?.name }}
                </span>
              </h4>
              
              <!-- Show action tags to connect when hovering problem -->
              <div v-if="hoveredProblemId" class="flex flex-wrap gap-2">
                <button
                  v-for="actionTag in actionTags"
                  :key="actionTag.id"
                  class="px-3 py-1.5 text-sm rounded-lg border transition-all flex items-center gap-1.5"
                  :class="[
                    hasCombination(hoveredProblemId, actionTag.id)
                      ? 'bg-n-teal-3 border-n-teal-7 text-n-teal-11'
                      : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-teal-6 hover:bg-n-teal-2'
                  ]"
                  @click="toggleCombination(hoveredProblemId, actionTag.id)"
                >
                  <i :class="hasCombination(hoveredProblemId, actionTag.id) ? 'i-lucide-link' : 'i-lucide-plus'" class="text-xs" />
                  {{ actionTag.name }}
                </button>
              </div>
              
              <!-- Show problem tags to connect when hovering action -->
              <div v-if="hoveredActionId" class="flex flex-wrap gap-2">
                <button
                  v-for="problemTag in problemTags"
                  :key="problemTag.id"
                  class="px-3 py-1.5 text-sm rounded-lg border transition-all flex items-center gap-1.5"
                  :class="[
                    hasCombination(problemTag.id, hoveredActionId)
                      ? 'bg-n-teal-3 border-n-teal-7 text-n-teal-11'
                      : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-teal-6 hover:bg-n-teal-2'
                  ]"
                  @click="toggleCombination(problemTag.id, hoveredActionId)"
                >
                  <i :class="hasCombination(problemTag.id, hoveredActionId) ? 'i-lucide-link' : 'i-lucide-plus'" class="text-xs" />
                  {{ problemTag.name }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Tags Column -->
        <div class="w-[320px] shrink-0 border-l border-n-weak overflow-y-auto p-4">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3 flex items-center gap-2 sticky top-0 bg-n-background py-2">
            <span class="w-3 h-3 rounded-full bg-n-amber-9"></span>
            Action Tags
            <span class="text-xs font-normal text-n-slate-11">({{ actionTags.length }})</span>
          </h3>
          <div class="space-y-1">
            <div
              v-for="(tag, index) in actionTags"
              :key="tag.id"
              :ref="el => { if (el) actionRefs[tag.id] = el }"
              class="group flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all"
              :class="[
                isActionHighlighted(tag.id)
                  ? 'border-n-amber-7 bg-n-amber-3 shadow-sm' 
                  : 'border-transparent hover:border-n-slate-6 hover:bg-n-alpha-1'
              ]"
              @mouseenter="hoveredActionId = tag.id"
              @mouseleave="hoveredActionId = null"
            >
              <span
                v-if="tag.protected"
                class="text-n-amber-9 shrink-0"
                title="Protected"
              >
                <i class="i-lucide-shield text-sm" />
              </span>
              <span class="flex-1 text-sm text-n-slate-12 font-medium truncate">
                {{ tag.name }}
              </span>
              <span
                v-if="tag.from_template"
                class="text-n-slate-10 shrink-0"
                title="From template"
              >
                <i class="i-lucide-file-text text-xs" />
              </span>
              <button
                class="opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-amber-9 transition-all shrink-0"
                title="Toggle protection"
                @click.stop="toggleProtected(tag)"
              >
                <i :class="tag.protected ? 'i-lucide-shield-off' : 'i-lucide-shield'" class="text-sm" />
              </button>
            </div>
            <div v-if="actionTags.length === 0" class="text-sm text-n-slate-11 text-center py-8">
              No action tags yet
            </div>
          </div>
        </div>
      </div>

      <!-- Footer with legend -->
      <div class="flex items-center justify-between px-6 py-3 border-t border-n-weak bg-n-alpha-1 shrink-0">
        <div class="flex items-center gap-6 text-xs text-n-slate-11">
          <div class="flex items-center gap-1.5">
            <i class="i-lucide-shield text-n-amber-9" />
            <span>Protected (won't auto-replace)</span>
          </div>
          <div class="flex items-center gap-1.5">
            <i class="i-lucide-file-text" />
            <span>From template</span>
          </div>
          <div class="flex items-center gap-1.5">
            <i class="i-lucide-link text-n-teal-9" />
            <span>Connected</span>
          </div>
        </div>
        <Button
          variant="faded"
          color="slate"
          @click="emit('close')"
        >
          Close
        </Button>
      </div>
    </div>
  </Modal>
</template>
